import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import * as LucideIcons from 'lucide-react';
import { Table } from '../../shared/table';
import { SearchBar } from '../../shared';
import { NeedsList } from '../../../types/needs';
import { mockNeedsLists } from '../../../data/mockNeedsData';
import { useNeedsTableConfig } from '../../Needs/hooks';

interface GroupNeedsProps {
  groupName: string;
}

export function GroupNeeds({ groupName }: GroupNeedsProps) {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');

  // Use the shared table config hook with group page flag
  const columns = useNeedsTableConfig({ isGroupPage: true });

  // Filter needs to current group and exclude past needs
  const groupNeeds = useMemo(() => {
    return mockNeedsLists.filter(need => 
      need.group === groupName && !need.isPastNeed
    );
  }, [groupName]);

  // Filter based on search query
  const filteredNeeds = useMemo(() => {
    if (!searchQuery.trim()) return groupNeeds;

    const query = searchQuery.toLowerCase();
    return groupNeeds.filter(need =>
      need.name.toLowerCase().includes(query) ||
      need.coordinator.toLowerCase().includes(query) ||
      need.nextItemNeeded.toLowerCase().includes(query)
    );
  }, [groupNeeds, searchQuery]);

  const handleRowClick = (needsList: NeedsList) => {
    navigate(`/needs/detail/${needsList.id}`);
  };

  return (
    <div>
      {/* Search Bar */}
      <div className="flex items-center gap-3 mb-6">
        <div style={{ flex: 1, minWidth: 0 }}>
          <SearchBar
            onSearchChange={setSearchQuery}
            placeholder="Search by name, coordinator, or item..."
            width="100%"
            dataTarget="needs-search-input"
          />
        </div>
        <button
          onClick={() => console.log('Create needs list')}
          className="inline-flex items-center justify-center gap-2 w-[38px] h-[38px] md:w-auto md:h-auto md:px-3 md:py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-sm hover:bg-gray-50 whitespace-nowrap"
          data-pp-at-target="create-needs-list-button"
        >
          <LucideIcons.Plus size={16} />
          <span className="hidden md:inline">Create Needs List</span>
        </button>
      </div>

      {/* Needs Table */}
      <Table<NeedsList>
        data={filteredNeeds}
        columns={columns}
        getRowKey={(need) => need.id}
        usePagination={true}
        pageSize={10}
        emptyMessage="No needs lists found for this group"
        onRowClick={handleRowClick}
      />
    </div>
  );
}
